import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import DealBuyButton from '../components/deal/shared-components/buy-buttons/DealBuyButton';
import DealGiftButton from '../components/deal/shared-components/buy-buttons/DealGiftButton';
import DealViewAvailabilityButton from '../components/deal/shared-components/buy-buttons/DealViewAvailabilityButton';
import {
  isNibbleEnabled,
  nibbleDwellTimer,
} from '../components/nibble/NibbleHelper';
import NibbleWidget from '../components/nibble/NibbleWidget';
import {
  BUY_EXCL,
  GIVE_AS_A_GIFT,
  NIBBLE_TIMER,
  PROCEED,
  RESERVE_CHECKOUT,
} from '../config/text/text';
import { trackEvent } from './analytics';

export const getBuyButtonDeal = ({
  selection,
  deal,
  convertBookingProductToBasketProduct,
  quantity,
  deposit,
  flights,
}) =>
  selection
    ? {
        ...deal,
        products: [
          convertBookingProductToBasketProduct(
            deal,
            selection,
            quantity,
            deposit,
            flights,
          ),
        ],
      }
    : null;

export const CalendarBuyButtons = ({
  deal,
  isBookingCalendarOnRedemption,
  isGift,
  buyButtonDeal,
  isButtonClickValid,
  selection,
  isExpressDealPage,
  buyButton,
  paymentData,
  selectedProductId,
  deposit,
  isNibbleAppeared,
  setIsNibbleAppeared,
}) => {
  const [nibbleVariantId, setNibbleVariantId] = useState(null);
  const [showNibble, setShowNibble] = useState(false);
  const [isVipUser] = useSelector((state) => [
    state?.user?.userprofile?.vipUser,
  ]);
  const hasTracked = useRef(false);
  const didTrackNibble = useRef(false);
  const didTrackNibblePromtClick = useRef(false);

  useEffect(() => {
    selectedProductId &&
      selectedProductId !== nibbleVariantId &&
      setNibbleVariantId(selectedProductId);
  }, [selectedProductId, nibbleVariantId]);

  // Nibble visibility and tracking
  useEffect(() => {
    if (!nibbleVariantId) return setShowNibble(false);

    const [wouldAppear, shouldAppear] = [true, false].map((flag) =>
      isNibbleEnabled(deal, nibbleVariantId, isVipUser, flag),
    );

    // Immediate prompt click tracking
    if (
      localStorage.getItem(NIBBLE_TIMER) === 'true' &&
      !didTrackNibblePromtClick.current
    ) {
      wouldAppear && trackEvent('nibble_button_would_appear_from_prompt_click');
      shouldAppear && trackEvent('nibble_button_appear_from_prompt_click');
      didTrackNibblePromtClick.current = wouldAppear || shouldAppear;
    }
    // Delayed appearance tracking
    nibbleDwellTimer().then(() => {
      if (
        wouldAppear &&
        !didTrackNibble.current &&
        !localStorage.getItem(NIBBLE_TIMER)
      ) {
        trackEvent('nibble_button_would_have_loaded');
        didTrackNibble.current = true;
      }

      if (shouldAppear) {
        if (!localStorage.getItem(NIBBLE_TIMER)) {
          !hasTracked.current && trackEvent('nibble_button_appear');
          hasTracked.current = true;
        }
        setShowNibble(true);
      } else {
        setShowNibble(false);
      }
    });
  }, [nibbleVariantId, deal, isVipUser]);

  // Reset tracking flag on component unmount
  useEffect(() => {
    return () => {
      hasTracked.current = false;
    };
  }, []);

  return (
    <>
      {/* Main Buy Button (Visible when not gifting) */}
      {!isGift && (
        <DealBuyButton
          buyButton={buyButton}
          className="buy-button margin size-larger full-width"
          deal={buyButtonDeal}
          deposit={deposit}
          isExpressDealPage={isExpressDealPage}
          isOptionSelected={selection !== null}
          isValidExternal={isButtonClickValid}
          nibbleAppeared={isNibbleAppeared}
          paymentInstruments={paymentData}
          selection={selection}
          text={isBookingCalendarOnRedemption ? 'BUY_EXCL' : 'PROCEED'}
        />
      )}

      {/* Nibble Widget Integration */}
      {selectedProductId && nibbleVariantId && showNibble && (
        <NibbleWidget
          deal={deal}
          setIsNibbleAppeared={setIsNibbleAppeared}
          variantId={nibbleVariantId}
        />
      )}

      {/* Gift Button (Visible when gifting) */}
      {isGift && (
        <DealGiftButton
          className="gift-button margin size-larger full-width"
          deal={buyButtonDeal}
          isValidExternal={isButtonClickValid}
          text="PROCEED"
        />
      )}
    </>
  );
};

export const TravelCalendarBuyButtons = ({
  isBookingCalendarOnRedemption,
  isBookingCalendarOnRedemptionGift,
  isGiftable,
  buttonRef,
  buyButtonDeal,
  deal,
  isButtonClickValid,
  deposit,
  isGoogleHotel,
  ito,
  searchParams,
  selectedProductId,
  setGiftingClearSelection,
}) => {
  const [nibbleVariantId, setNibbleVariantId] = useState(null);
  const [showNibble, setShowNibble] = useState(false);
  const [isVipUser] = useSelector((state) => [
    state?.user?.userprofile?.vipUser,
  ]);
  const hasTracked = useRef(false);
  const didTrackNibble = useRef(false);
  const didTrackNibblePromtClick = useRef(false);

  useEffect(() => {
    const variantId = selectedProductId;
    variantId && variantId !== nibbleVariantId && setNibbleVariantId(variantId);
  }, [selectedProductId, nibbleVariantId]);

  // Nibble visibility and tracking
  useEffect(() => {
    if (!nibbleVariantId) return setShowNibble(false);

    const checkNibble = (flag) =>
      isNibbleEnabled(deal, nibbleVariantId, isVipUser, flag);
    const [wouldLoad, shouldAppear] = [true, false].map(checkNibble);

    // Immediate prompt tracking
    if (
      localStorage.getItem(NIBBLE_TIMER) === 'true' &&
      !didTrackNibblePromtClick.current
    ) {
      wouldLoad && trackEvent('nibble_button_would_appear_from_prompt_click');
      shouldAppear && trackEvent('nibble_button_appear_from_prompt_click');
      didTrackNibblePromtClick.current = wouldLoad || shouldAppear;
    }
    // Delayed appearance tracking
    nibbleDwellTimer().then(() => {
      wouldLoad &&
        !didTrackNibble.current &&
        !localStorage.getItem(NIBBLE_TIMER) &&
        trackEvent('nibble_button_would_have_loaded');
      didTrackNibble.current = didTrackNibble.current || wouldLoad;

      if (shouldAppear) {
        if (!localStorage.getItem(NIBBLE_TIMER)) {
          !hasTracked.current && trackEvent('nibble_button_appear');
          hasTracked.current = true;
        }
        setShowNibble(true);
      } else {
        setShowNibble(false);
      }
    });
  }, [nibbleVariantId, deal, isVipUser]);

  useEffect(() => {
    return () => {
      hasTracked.current = false;
    };
  }, []);

  return (
    <>
      {/* Main Buy Button */}
      <DealBuyButton
        buttonRef={buttonRef}
        className="buy-button margin size-xlarge full-width"
        deal={buyButtonDeal}
        isValidExternal={isButtonClickValid}
        text={
          deposit
            ? RESERVE_CHECKOUT
            : isBookingCalendarOnRedemption
            ? BUY_EXCL
            : PROCEED
        }
      />

      {/* Nibble Widget Integration */}
      {selectedProductId && nibbleVariantId && showNibble && (
        <NibbleWidget deal={deal} variantId={nibbleVariantId} />
      )}

      {/* Giftable Logic */}
      {isGiftable && (
        <>
          {(deal?.display?.dynamicTravelDeal ||
            isBookingCalendarOnRedemptionGift) && (
            <DealViewAvailabilityButton
              className="full-width"
              deal={deal}
              gift
              isGoogleHotel={isGoogleHotel}
              ito={ito}
              searchParams={searchParams}
              setGiftingClearSelection={setGiftingClearSelection}
            />
          )}
          {!isBookingCalendarOnRedemptionGift &&
            !deal?.display?.dynamicTravelDeal && (
              <DealGiftButton
                className="gift-button size-larger full-width outline margin"
                deal={buyButtonDeal}
                isValidExternal={isButtonClickValid}
                text={GIVE_AS_A_GIFT}
              />
            )}
        </>
      )}
    </>
  );
};

CalendarBuyButtons.propTypes = {
  buyButtonDeal: PropTypes.object,
  isBookingCalendarOnRedemption: PropTypes.bool,
  isButtonClickValid: PropTypes.bool,
  isGift: PropTypes.bool,
};

TravelCalendarBuyButtons.propTypes = {
  buttonRef: PropTypes.any,
  buyButtonDeal: PropTypes.object,
  deal: PropTypes.object,
  deposit: PropTypes.any,
  isBookingCalendarOnRedemption: PropTypes.bool,
  isBookingCalendarOnRedemptionGift: PropTypes.bool,
  isButtonClickValid: PropTypes.bool,
  isGiftable: PropTypes.bool,
  isGoogleHotel: PropTypes.bool,
  searchParams: PropTypes.object,
};
